import { Stack } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import { Button, buttonStylesPrimary, ConfirmDialog, detailsHeaderStylesBasic, type SortInfo } from '@h2oai/ui-kit';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  restrictionTooltipMessage,
  usePermissions,
} from '../../../authz/providers/PermissionsProvider/PermissionsProvider';
import { AddEditModelForm } from '../../../components/AdminSettings/components/AddEditModelForm/AddEditModelForm';
import { EntityModelItemActionButton } from '../../../components/AdminSettings/components/EntityModelItemActionButton';
import { useEntity } from '../../../components/AdminSettings/Entity/hooks';
import type { Entity } from '../../../components/AdminSettings/Entity/types';
import { getIdFromName } from '../../../components/AdminSettings/utils';
import type { ReorderItem } from '../../../components/ListPages/List';
import List from '../../../components/ListPages/List';
import ListPage from '../../../components/ListPages/ListPage';
import { defaultBasePath, defaultWorkspaceName } from '../../../notebook/constants';
import { NotebookEntitiesMap } from '../../../notebook/entity/services';
import { NotebookKernelEntityType } from '../../../notebook/entity/types';
import { stackStylesInnerPivotPage } from '../../../themes/themes';
import { useCloudPlatformDiscovery } from '../../../utils/hooks';
import { actionButtonStyles } from '../../AIEngineSettingsPage/AIEMAdminSettingsPage.styles';
import { KernelSettingsEditPanel } from './KernelSettingsEditPanel';
import { NotebookSettingsViewPanel } from './NotebookSettingsViewPanel';
import type { NotebookEntityDataContainerProps, NotebookEntityModel } from './types';

export const NotebookEntityDataContainer = <EntityModel extends NotebookEntityModel>(
  props: NotebookEntityDataContainerProps<EntityModel>
) => {
  const { entity, apiKey = 'notebookApiUrl', userActions } = props;
  const [canCreate, canView, canList, canEdit, canDelete, permissionsLoading] = usePermissions(
    userActions || [],
    'workspaces/global'
  );
  const ignorePermissions = !userActions;
  const { columns, canBeReordered, displayName, sortFields, actions } = entity;
  const { List: hasList } = actions || undefined;

  const cloudPlatformDiscovery = useCloudPlatformDiscovery();

  const requestPath = Object(cloudPlatformDiscovery)[apiKey] || defaultBasePath;

  const entityDataConnector = useEntity<NotebookKernelEntityType>({
    entitiesMap: NotebookEntitiesMap,
    requestPath,
  });

  const operationCreate = useRef<boolean>(false);
  const showNoDataMessage = useRef<boolean>(false);
  const refSort = useRef<SortInfo<EntityModel & { [key: string]: any }>>();

  const [data, setData] = useState<EntityModel[] | undefined>([]);
  const [dataRetrievalError, setDataRetrievalError] = useState<string>();
  const [selectedItem, setSelectedItem] = useState<EntityModel>();

  const [isLoading, { setTrue: showLoading, setFalse: hideLoading }] = useBoolean(false);
  const [isViewPanelOpen, { setTrue: showViewPanel, setFalse: hideViewPanel }] = useBoolean(false);
  const [isEditPanelOpen, { setTrue: showEditPanel, setFalse: hideEditPanel }] = useBoolean(false);
  const [isDeleteDialogOpen, { setTrue: showDeleteDialog, setFalse: hideDeleteDialog }] = useBoolean(false);

  const updatedColumns = useMemo(() => {
    return [...columns].map((c) => {
      const column = { ...c };
      // action button
      if (column.key === 'actions') {
        const deleteFn = (e: EntityModel) => {
          setSelectedItem(e);
          showDeleteDialog();
        };
        const openViewPanel = (e: EntityModel) => {
          setSelectedItem(e);
          showViewPanel();
        };
        const openEditPanel = (e: EntityModel) => {
          setSelectedItem(e);
          showEditPanel();
          operationCreate.current = false;
        };
        column.onRender = (model: EntityModel) => (
          <Stack horizontal tokens={{ childrenGap: 16 }} horizontalAlign="end" verticalAlign="center">
            <Button
              text="View"
              onClick={!canView && !ignorePermissions ? () => null : () => openViewPanel(model)}
              disabled={!canView && !ignorePermissions}
              tooltip={restrictionTooltipMessage(!canView && !ignorePermissions)}
            />

            <EntityModelItemActionButton
              model={model}
              onSetLatest={onSetLatest}
              onEdit={openEditPanel}
              onView={openViewPanel}
              onDelete={deleteFn}
              entity={entity}
              disableEdit={!canEdit && !ignorePermissions}
              disableDelete={!canDelete && !ignorePermissions}
            />
          </Stack>
        );
      }
      return column;
    });
  }, [columns, canEdit, canView, canDelete, ignorePermissions]);

  const onDismissDeleteDialog = useCallback(() => {
    hideDeleteDialog();
    setSelectedItem(undefined);
  }, [hideDeleteDialog, setSelectedItem]);
  const onCreateClick = useCallback(() => {
    setSelectedItem(entity.emptyModel);
    operationCreate.current = true;
    showEditPanel();
  }, [setSelectedItem, operationCreate.current, showEditPanel]);

  const onReorderItem = async ({ item, newOrder }: ReorderItem<EntityModel>) => {
    showLoading();
    try {
      const name = item.name;
      const requestParams = {
        name,
        newOrder,
      };
      const entities = await entityDataConnector.reorder(entity, requestParams, {
        subject: getIdFromName(name),
        action: `reordered to position number ${newOrder} `,
      });
      setData(entities);
    } catch (error: any) {
      setDataRetrievalError(error.message);
    }
    hideLoading();
  };
  const getDeleteDialogContent = () => {
    return (
      <Stack>
        <p>
          Are you sure you want to delete {entity.displayName}{' '}
          <span style={{ fontWeight: 'bold' }}>{selectedItem ? getIdFromName(selectedItem.name) : ''}</span> ?
        </p>
      </Stack>
    );
  };
  const onDelete = async () => {
    showLoading();
    const name = selectedItem?.name;
    const cleanName = getIdFromName(name);
    if (!selectedItem || !name) {
      setDataRetrievalError('Missing entity name.');
      hideDeleteDialog();
      setSelectedItem(undefined);
      hideLoading();
      return;
    }
    try {
      const messageText = {
        subject: cleanName,
        action: 'deleted ',
      };
      await entityDataConnector.erase(entity, { name: name }, messageText);
      loadData();
    } catch (error: any) {
      // Ignore the error for removing a used Kernel Image or Template
      if (error.code !== 13) setDataRetrievalError(error.message);
    }
    hideDeleteDialog();
    setSelectedItem(undefined);
    hideLoading();
  };

  const loadData = async () => {
    showLoading();
    try {
      const requestParams = {
        pageSize: 1000,
        parent: defaultWorkspaceName,
      };
      if (sortFields) {
        requestParams['orderBy'] = refSort.current?.isSorted
          ? `${String(refSort.current?.fieldName)} ${refSort.current?.isSortedDescending ? 'desc' : 'asc'}`
          : '';
      }
      const entities = await entityDataConnector.list(entity, requestParams);
      if (!entities || (entities as EntityModel[]).length === 0) {
        showNoDataMessage.current = true;
        setData([]);
      } else {
        showNoDataMessage.current = false;
        setData(entities);
      }
    } catch (error: any) {
      setDataRetrievalError(error.message);
    }
    hideLoading();
  };

  const onSave = () => {
    hideEditPanel();
    loadData();
  };

  const onSetLatest = async (model: EntityModel) => {
    showLoading();
    try {
      await entityDataConnector.setLatest(entity, model);
      loadData();
    } catch (error: any) {
      setDataRetrievalError(error.message);
    }
    hideLoading();
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <Stack tokens={{ childrenGap: 5 }} styles={{ root: { paddingTop: 5 } }}>
      <Stack horizontal horizontalAlign="end">
        <Button
          styles={[buttonStylesPrimary]}
          buttonContainerStyles={actionButtonStyles}
          text={entity.createButtonLabel || `Add ${entity.displayName}`}
          onClick={!canCreate && !ignorePermissions ? () => null : onCreateClick}
          disabled={!canCreate && !ignorePermissions}
          tooltip={restrictionTooltipMessage(!canCreate && !ignorePermissions)}
        />
      </Stack>
      {hasList && (
        <ListPage
          showFilterButton={false}
          showNoDataPage={showNoDataMessage.current}
          showSearchInput={false}
          error={
            !permissionsLoading && !canList && !ignorePermissions
              ? 'You are not authorized to access AI Engine Settings in this Workspace.'
              : dataRetrievalError
          }
          loading={isLoading}
          copy={{
            title: 'Notebook Admin Settings',
            subtitle: '',
            loadingMessage: `Loading ${entity.displayName}s`,
            noDataMessage: 'No data.',
          }}
          showPageTitle={false}
          stackStyles={stackStylesInnerPivotPage}
        >
          <List<EntityModel>
            dataTest="entity-model-list"
            items={data || []}
            columns={updatedColumns}
            canBeReordered={canBeReordered}
            onReorderItem={onReorderItem}
            orderField={'order'}
            onShouldVirtualize={process.env.NODE_ENV === 'test' ? () => false : undefined}
            getKey={(col) => col.key}
            isHeaderVisible={true}
            headerStyles={detailsHeaderStylesBasic}
            showSelectAllButton={false}
            sortFields={sortFields}
          />
          {isViewPanelOpen && (
            <NotebookSettingsViewPanel
              data-test="settings-view-panel"
              panelTitle={displayName}
              item={selectedItem}
              onDismiss={hideViewPanel}
              type={entity.type}
            />
          )}
          <ConfirmDialog
            confirmationButtonText={'Delete'}
            hidden={!isDeleteDialogOpen}
            content={getDeleteDialogContent()}
            title={`Delete ${entity.displayName}`}
            onConfirm={onDelete}
            onDismiss={onDismissDeleteDialog}
          />
        </ListPage>
      )}
      {isEditPanelOpen && (
        <KernelSettingsEditPanel
          requestPath={requestPath}
          panelTitle={operationCreate.current ? `Add ${displayName}` : `Edit ${displayName}`}
          onDismiss={() => {
            hideEditPanel();
            operationCreate.current = false;
          }}
          isCreate={operationCreate.current}
          onSave={onSave}
          entity={entity as unknown as Entity<NotebookEntityModel, NotebookKernelEntityType>}
          model={selectedItem}
        />
      )}
      {!hasList && (
        <AddEditModelForm<EntityModel, NotebookKernelEntityType>
          requestPath={requestPath}
          entitiesMap={NotebookEntitiesMap}
          entity={entity}
          onSave={() => {
            throw new Error('Function not implemented.');
          }}
          onDismiss={() => {}}
          isCreate={false}
        />
      )}
    </Stack>
  );
};
